import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useOrganization } from '../../context/organization';
import StandardPage from '../../components/StandardPage';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { handleApiError } from '../../api-client/error_handler';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const SdkConfigurationPage: React.FC = () => {
  const { organization } = useOrganization();
  const { getAccessTokenSilently } = useAuth0();
  const [domains, setDomains] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [newDomain, setNewDomain] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [copyStatus, setCopyStatus] = useState<string>('');
  const navigate = useNavigate();

  const orgId = organization?.id;

  const scriptSnippet = useMemo(() => {
    if (!orgId) return '';
    return `<script async defer src="https://static.civapi.com/js-sdk.js?orgId=${orgId}"></script>`;
  }, [orgId]);

  const fetchDomains = useCallback(async () => {
    if (!API_BASE_URL || !organization) return;
    try {
      setLoading(true);
      const token = await getAccessTokenSilently();
      const { data } = await axios.get(`${API_BASE_URL}/sdk/domains`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDomains(data.domains ?? []);
      setError(null);
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, organization]);

  useEffect(() => {
    fetchDomains();
  }, [fetchDomains]);

  const updateDomains = useCallback(async (updated: string[]) => {
    if (!API_BASE_URL) return;
    setSaving(true);
    try {
      const token = await getAccessTokenSilently();
      const { data } = await axios.put(
        `${API_BASE_URL}/sdk/domains`,
        { domains: updated },
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setDomains(data.domains ?? []);
      setNewDomain('');
      setError(null);
    } catch (err) {
      toast.error(handleApiError(err));
    } finally {
      setSaving(false);
    }
  }, [getAccessTokenSilently]);

  const handleAddDomain = (event: React.FormEvent) => {
    event.preventDefault();
    if (!newDomain.trim()) return;
    const updated = Array.from(new Set([...domains, newDomain.trim()]));
    updateDomains(updated);
  };

  const handleRemoveDomain = (domain: string) => {
    const updated = domains.filter(item => item !== domain);
    updateDomains(updated);
  };

  const handleCopy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyStatus('Copied!');
      setTimeout(() => setCopyStatus(''), 2000);
    } catch (err) {
      console.error('[SDK Configuration] copy failed', err);
      setCopyStatus('Unable to copy');
    }
  };

  if (!organization) {
    return <StandardPage title="SDK Configuration"><p>Loading organization data...</p></StandardPage>;
  }

  if (!organization.has_sdk_license) {
    return (
      <StandardPage title="SDK Configuration">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900">
          <h2 className="text-xl font-semibold mb-2">SDK license required</h2>
          <p>Your organization does not have an SDK license enabled. Please contact CivAPI to activate SDK access before configuring domains or contexts.</p>
        </div>
      </StandardPage>
    );
  }

  return (
    <StandardPage title="SDK Configuration">
      <div className="space-y-8">
        <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">Domain whitelist</h2>
              <p className="text-sm text-slate-500">Only these domains may load the CivAPI SDK script.</p>
            </div>
          </div>
          {error && <p className="text-sm text-rose-600 mb-3">{error}</p>}
          {loading ? (
            <p className="text-slate-500">Loading domains...</p>
          ) : (
            <>
              <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-100 bg-slate-50/50">
                {domains.length === 0 && <li className="px-4 py-3 text-sm text-slate-500">No domains added yet.</li>}
                {domains.map(domain => (
                  <li key={domain} className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="font-mono text-slate-700">{domain}</span>
                    <button
                      className="text-rose-600 text-xs font-medium hover:underline"
                      onClick={() => handleRemoveDomain(domain)}
                      disabled={saving}
                    >
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
              <form onSubmit={handleAddDomain} className="mt-4 flex flex-col gap-3 sm:flex-row">
                <input
                  type="text"
                  placeholder="e.g. www.example.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="rounded-xl bg-blue-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 disabled:opacity-60"
                  disabled={saving || !newDomain.trim()}
                >
                  Add domain
                </button>
              </form>
            </>
          )}
        </section>

        <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">Embed script</h2>
          <p className="text-sm text-slate-500 mb-4">
            Paste this snippet onto any whitelisted site to load the CivAPI SDK. The script exposes <code className="font-mono text-xs bg-slate-100 px-1 py-0.5 rounded">window.openCivapi()</code> for launching contexts programmatically.
          </p>
          <div className="relative">
            <pre className="rounded-2xl bg-slate-900 p-4 text-sm text-slate-100 overflow-x-auto">
              <code>{scriptSnippet}</code>
            </pre>
            <button
              type="button"
              className="absolute top-3 right-3 rounded-lg border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white backdrop-blur hover:bg-white/20"
              onClick={() => handleCopy(scriptSnippet)}
            >
              Copy
            </button>
          </div>
          {copyStatus && <p className="mt-2 text-sm text-slate-500">{copyStatus}</p>}
        </section>

        <section className="rounded-3xl border border-blue-100 bg-blue-50/70 p-6">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h3 className="text-base font-semibold text-blue-900">Manage CivAPI contexts</h3>
              <p className="text-sm text-blue-800/80">Create contextual experiences and copy the HTML needed to launch them.</p>
            </div>
            <a
              onClick={() => navigate('/sdk/contexts')}
              className="inline-flex items-center justify-center rounded-xl border border-blue-500/20 bg-white px-4 py-2 text-sm font-semibold text-blue-600 shadow-sm hover:bg-blue-50 cursor-pointer"
            >
              Go to contexts
            </a>
          </div>
        </section>
      </div>
    </StandardPage>
  );
};

export default SdkConfigurationPage;
